import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { IScenarioDataModel } from '../../data-models/scenario.data-model';
import { fetchScenariosForCompany } from '../queries/MaggieScenarioQueries';

type ScenariosValue = IScenarioDataModel[] | null | Promise<IScenarioDataModel[] | null>;

/**
 * Default fetches all scenarios for the company. Returns `null` (without
 * fetching) when `companyId <= 0` — `useScenarioActions` and `ScenariosWrapper`
 * subscribe via `selectedCompanyIdProfileAtom`, which defaults to `-1` before
 * a profile is selected.
 */
export const scenariosByCompanyIdStateJ = atomFamily((companyId: number) =>
  atomWithDefault<ScenariosValue>(async () => {
    if (companyId <= 0) {
      return null;
    }
    return fetchScenariosForCompany(companyId);
  })
);

/**
 * `loadable`-wrapped variant for callers that need a non-suspending peek.
 */
export const scenariosByCompanyIdStateLoadableJ = atomFamily((companyId: number) =>
  loadable(scenariosByCompanyIdStateJ(companyId))
);

/**
 * Map of scenario id → scenario for the company.
 */
export const scenariosByIdMapStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<Map<number, IScenarioDataModel>> => {
    const scenarios = await get(scenariosByCompanyIdStateJ(companyId));
    const map = new Map<number, IScenarioDataModel>();
    if (!scenarios) {
      return map;
    }
    for (const scenario of scenarios) {
      map.set(scenario.id, scenario);
    }
    return map;
  })
);

/**
 * Scenario currently being edited / viewed / created.
 *
 * If there is a scenarioId search param the consumer (`useSetScenarioStateFromPath`)
 * sets this atom to the matching scenario; otherwise it sets the initial form
 * values for a new scenario. Reset-on-unmount is handled in the consumer.
 */
export const currentScenarioStateJ = atom<Partial<IScenarioDataModel> | null>(null);
